import type { CheckoutFormValuesType } from '@ecomm/checkout/shipping-schema'
import { useCartLoaded, useCartShippingAddress } from '@ecomm/data-cart'
import classNames from 'classnames'
import { useFormikContext } from 'formik'
import { useCallback } from 'react'

import { useTrackingConfirmAddressShippingModalValidation } from '@ecomm/tracking'
import { Modal } from '../Modal'
import { AddressValidationModalPicker } from './AddressValidationModalPicker'
import { formatAddress } from './formatters'
import { useModalSubmit } from './useModalSubmit'

type AddressValidationModalProps = {
  readonly isOpen: boolean
  readonly setOpen: (value: boolean) => void
  readonly redirectUrl: string
}

export function AddressValidationModal({
  isOpen,
  setOpen,
  redirectUrl
}: AddressValidationModalProps) {
  const shippingAddress = useCartShippingAddress()
  const { values } = useFormikContext<CheckoutFormValuesType>()
  const onSubmit = useModalSubmit(redirectUrl, values.sameAddress)
  const cartLoaded = useCartLoaded()
  const trackConfirmAddress = useTrackingConfirmAddressShippingModalValidation()

  const onChangeAddressClick = useCallback(() => {
    trackConfirmAddress('Change address')
    setOpen(false)
  }, [setOpen, trackConfirmAddress])

  return (
    <Modal isOpen={isOpen} onClose={() => setOpen(false)}>
      <div
        className="flex w-full flex-col p-8 pb-2 md:pb-8"
        data-component="modal_content"
        data-testid="modal_content"
      >
        <div className="m-0 mb-6 flex flex-col gap-2 md:mb-0">
          <h3 className="m-0 text-2xl font-medium md:font-bold lg:text-4xl">
            Confirm your address
          </h3>
          <p className="text-neutral-black m-0 text-base lg:text-lg">
            To ensure delivery accuracy, we suggest the address displayed below.
          </p>
          <p
            className="m-0 w-full text-base lg:text-lg"
            data-component="user_entered_address"
          >
            <span className="font-bold"> You entered: </span>
            {shippingAddress ? formatAddress(shippingAddress) : null}
          </p>
        </div>
        <div className="flex h-full w-full flex-col items-center gap-8 md:flex-row md:gap-10 lg:gap-24">
          <AddressValidationModalPicker />
          <div className="flex h-full w-full flex-col md:mt-20">
            <button
              className={classNames('btn w-full md:w-auto md:px-8', {
                'btn-solid-primary': cartLoaded,
                'btn-disabled': !cartLoaded
              })}
              data-component="ValidateButton"
              disabled={!cartLoaded}
              onClick={onSubmit}
            >
              <span className="justify-center text-base font-medium lg:text-lg">
                Continue with verified address
              </span>
            </button>
            <button
              className="text-btn-primary m-4 cursor-pointer border-none bg-white text-center text-base font-medium lg:text-lg"
              onClick={onChangeAddressClick}
            >
              Change Address
            </button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
